import { useLayoutEffect, useMemo, useRef, type CSSProperties, type ReactElement } from 'react'
import { twMerge } from 'tailwind-merge'
import { FadingScrollable } from '@renderer/chat/components/elements/FadingScrollable'
import {
  chooseScrollMinute,
  getMinuteOfDay,
  layoutDayEvents,
  MINUTES_PER_DAY,
  type DayCalendarEvent,
} from '@renderer/components/calendar-day-view/calendar-day-layout'

export type { DayCalendarEvent, CalendarTimeRange } from '@renderer/components/calendar-day-view/calendar-day-layout'

const HOUR_HEIGHT = 72
const COMPACT_HOUR_HEIGHT = 56
const SCROLL_MARGIN_PX = 12
const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
const hourFormatter = new Intl.DateTimeFormat(undefined, { hour: 'numeric' })

export function formatCalendarEventTime(event: DayCalendarEvent): string {
  if (event.isAllDay) {
    return 'All day'
  }
  const formatter = event.start.getMinutes() === 0 && event.end.getMinutes() === 0 ? hourFormatter : timeFormatter
  const startParts = formatter.formatToParts(event.start)
  const endParts = formatter.formatToParts(event.end)
  const startPeriod = startParts.find((part) => part.type === 'dayPeriod')?.value
  const endPeriod = endParts.find((part) => part.type === 'dayPeriod')?.value
  const start = startParts
    .filter((part) => part.type !== 'dayPeriod' || startPeriod !== endPeriod)
    .map((part) => part.value)
    .join('')
    .trim()
  return `${start} – ${formatter.format(event.end)}`
}

export interface CalendarDayViewProps {
  day: Date
  events: readonly DayCalendarEvent[]
  selectedEventId?: string
  now?: Date
  compact?: boolean
  className?: string
}

export function CalendarDayView({
  day,
  events,
  selectedEventId,
  now,
  compact = false,
  className,
}: CalendarDayViewProps): ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null)
  const dayKey = day.toDateString()
  const { allDayEvents, timedEvents } = useMemo(() => layoutDayEvents(day, events), [day, events])
  const nowMinute = !compact && now?.toDateString() === dayKey ? getMinuteOfDay(now) : null
  const hourHeight = compact ? COMPACT_HOUR_HEIGHT : HOUR_HEIGHT
  const pixelsPerMinute = hourHeight / 60
  const gutterLabelClass = compact ? 'right-full mr-1 text-right' : 'type-ui-xs right-full mr-2 text-right'

  // Re-scrolls only when the target moves, so a calendar sync that changes
  // nothing on screen leaves the user's own scrolling alone. The view's height
  // feeds the target, and a parent sized late (a popover before it's positioned)
  // first lays the timeline out at its full height, so a resize re-runs it too.
  const scrolledToRef = useRef<string>()
  useLayoutEffect(() => {
    const scroll = scrollRef.current
    if (!scroll) {
      return
    }
    const scrollToBestMinute = () => {
      const minute = chooseScrollMinute(
        timedEvents,
        selectedEventId,
        scroll.clientHeight / pixelsPerMinute,
        SCROLL_MARGIN_PX / pixelsPerMinute,
      )
      const target = `${dayKey}:${minute}`
      if (scrolledToRef.current !== target) {
        scrolledToRef.current = target
        scroll.scrollTop = minute * pixelsPerMinute
      }
    }
    scrollToBestMinute()
    const observer = new ResizeObserver(scrollToBestMinute)
    observer.observe(scroll)
    return () => observer.disconnect()
  }, [dayKey, timedEvents, selectedEventId, pixelsPerMinute])

  return (
    <section
      aria-label={`Calendar for ${day.toLocaleDateString(undefined, { dateStyle: 'full' })}`}
      className={twMerge(
        'bg-background-raised text-foreground flex min-h-0 min-w-0 flex-col overflow-hidden',
        compact && 'corner-smooth m-2 rounded-lg p-1 text-[10px] leading-3 shadow-md',
        className,
      )}
    >
      {(!compact || allDayEvents.length > 0) && (
        <div className="flex shrink-0 border-b">
          <span
            className={twMerge(
              'text-foreground-secondary shrink-0',
              compact ? 'w-6 py-1 pr-1 text-right' : 'type-ui-xs w-16 px-2 py-2 text-right',
            )}
          >
            All day
          </span>
          <div
            className={twMerge(
              'flex min-w-0 flex-1 flex-col',
              compact ? 'gap-0.5 p-0.5' : 'gap-1 border-l px-1 py-1.5',
            )}
          >
            {allDayEvents.map((event) => (
              <CalendarEventBlock
                key={event.id}
                event={event}
                selected={selectedEventId === event.id}
                compact={compact}
                className={twMerge('min-w-0 max-w-full', !compact && 'py-0.5')}
              />
            ))}
          </div>
        </div>
      )}
      <FadingScrollable ref={scrollRef} className="min-h-0 flex-1 overscroll-contain" aria-label="Day timeline">
        <div
          className={twMerge('relative', compact ? 'ml-6' : 'ml-16 border-l')}
          style={{ height: MINUTES_PER_DAY * pixelsPerMinute }}
        >
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="absolute inset-x-0 border-t"
              style={{ top: hour * hourHeight, height: hourHeight }}
            >
              <span
                className={twMerge(
                  'text-foreground-secondary absolute top-0 w-max -translate-y-1/2 tabular-nums',
                  gutterLabelClass,
                )}
              >
                {compact
                  ? `${hour % 12 || 12}${hour < 12 ? 'a' : 'p'}`
                  : new Date(2000, 0, 1, hour).toLocaleTimeString(undefined, { hour: 'numeric' })}
              </span>
            </div>
          ))}
          {timedEvents.map(({ event, startMinute, endMinute, column, columnCount }) => {
            const height = Math.max(2, (endMinute - startMinute) * pixelsPerMinute - (compact ? 1 : 2))
            return (
              <CalendarEventBlock
                key={event.id}
                event={event}
                selected={selectedEventId === event.id}
                showTime={endMinute - startMinute >= (compact ? 60 : 40)}
                dense={height < (compact ? 18 : 28)}
                compact={compact}
                className="absolute"
                style={{
                  top: startMinute * pixelsPerMinute,
                  height,
                  left: `calc(${(column / columnCount) * 100}% + ${compact ? 1 : 3}px)`,
                  width: `calc(${100 / columnCount}% - ${compact ? 3 : 7}px)`,
                }}
              />
            )
          })}
          {nowMinute !== null && (
            <div
              aria-label={`Current time ${timeFormatter.format(now)}`}
              className="border-danger pointer-events-none absolute inset-x-0 z-20 border-t"
              style={{ top: nowMinute * pixelsPerMinute }}
            >
              <span
                className={twMerge(
                  'bg-background-raised text-danger-subtle absolute top-0 w-max -translate-y-1/2 font-medium tabular-nums',
                  gutterLabelClass,
                )}
              >
                {timeFormatter.format(now)}
              </span>
              <span className="bg-danger absolute -left-1 -top-1 size-2 rounded-full" />
            </div>
          )}
        </div>
      </FadingScrollable>
    </section>
  )
}

interface CalendarEventBlockProps {
  event: DayCalendarEvent
  selected: boolean
  showTime?: boolean
  /** Too short for the padded title line, so the title sits tight and centered. */
  dense?: boolean
  compact: boolean
  className?: string
  style?: CSSProperties
}

function CalendarEventBlock({ event, selected, showTime, dense, compact, className, style }: CalendarEventBlockProps) {
  const label = `${event.title || 'Untitled event'}, ${formatCalendarEventTime(event)}${event.calendarName ? `, ${event.calendarName}` : ''}`
  const classes = twMerge(
    'type-ui-sm relative flex flex-col items-start justify-start overflow-hidden text-left',
    compact ? 'rounded-[2px] py-px pl-[7px] pr-1 leading-4' : 'rounded-[4px] py-1 pl-3 pr-2',
    dense && 'justify-center py-0 leading-none',
    dense && compact && 'text-[11px] leading-3',
    '[--event-text-lightness:0.45] dark:[--event-text-lightness:0.8]',
    'bg-[var(--event-background)] text-[color:var(--event-foreground)]',
    selected && 'z-10 ring-1 ring-[color:var(--event-background)] ring-inset',
    className,
  )
  const eventStyle = {
    ...style,
    '--event-color': event.color ?? 'var(--info)',
    '--event-background': selected
      ? 'oklch(from var(--event-color) calc(l * 0.95) c h / 1)'
      : 'color-mix(in oklab, var(--event-color) 10%, var(--background-raised))',
    '--event-foreground': selected
      ? 'var(--on-dark-text)'
      : 'oklch(from var(--event-color) var(--event-text-lightness) min(c, 0.16) h / 1)',
  } as CSSProperties
  return (
    <div aria-label={label} title={label} className={classes} style={eventStyle}>
      {!selected && (
        <span
          aria-hidden="true"
          className={twMerge(
            'absolute rounded-full bg-[var(--event-color)]',
            compact ? 'inset-y-0.5 left-0.5 w-0.5' : 'inset-y-1 left-1 w-[3px]',
          )}
        />
      )}
      <span className="block w-full truncate font-medium">{event.title || 'Untitled event'}</span>
      {showTime && <span className="block w-full truncate tabular-nums">{formatCalendarEventTime(event)}</span>}
    </div>
  )
}
